import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import BorderStyleDashed from '@/assets/icons/cg_border_style_dashed.svg?component';
import TrendingDown from '@/assets/icons/md_trending_down.svg?component';
import TrendingFlat from '@/assets/icons/md_trending_flat.svg?component';
import TrendingUp from '@/assets/icons/md_trending_up.svg?component';
import { MetricValueLink } from '@/components/atoms/MetricValueLink';
import { type IconComponent, SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';
import { alertIconColors } from '@/config/settings';
import {
    formatFractionAsPercentageByLocale,
    formatNumberByLocale,
    parseLocale,
} from '@/utils/localeFormatters';

import { describeTrend, type TrendDirection, trendHelpText } from './describeTrend';

/** Matches legacy welfare wound column (`DEFAULT_EQ_THRESHOLD` / welfare overview). */
export const OVERVIEW_WOUND_GROWTH_LIMIT = 0.025;

interface TrendValueProps {
    displayValue: string;
    icon: IconComponent;
    iconColor: string;
    /** When set, the value itself becomes a link to this page. */
    to?: string;
    className?: string;
}

function TrendValue(props: TrendValueProps) {
    const { displayValue, icon, iconColor, to, className } = props;

    /*
     * A `div`, matching the element OptoTooltip used to wrap children in. A
     * `span` here would newly match the overview table's
     * `td[data-name='lice'] span` margin rule, which reached only into the old
     * in-flow bubble and nothing visible.
     */
    return (
        <div className={className}>
            {to ? (
                <MetricValueLink to={to}>{displayValue}</MetricValueLink>
            ) : (
                <span>{displayValue}</span>
            )}
            <SvgIcon icon={icon} size="inherit" color={iconColor} />
        </div>
    );
}

const TREND_ICON: Record<TrendDirection, [string, IconComponent]> = {
    noData: [alertIconColors.neutral, BorderStyleDashed],
    stable: [alertIconColors.neutral, TrendingFlat],
    growing: [alertIconColors.danger, TrendingUp],
    decreasing: [alertIconColors.success, TrendingDown],
};

export interface OverviewValueWithTrendTooltipProps {
    value?: number;
    growth?: number | null;
    growthLimit?: number;
    showPercentage: boolean;
    to?: string;
    className?: string;
}

export default function OverviewValueWithTrendTooltip(
    props: OverviewValueWithTrendTooltipProps
): ReactElement {
    const {
        value,
        growth,
        growthLimit = OVERVIEW_WOUND_GROWTH_LIMIT,
        showPercentage,
        to,
        className,
    } = props;

    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);
    const hasGrowth = growth != null && !Number.isNaN(growth);
    const growthNumber = hasGrowth ? growth : 0;

    const showValue = showPercentage
        ? Number.isNaN(value) || value == null
            ? ''
            : formatFractionAsPercentageByLocale(value, locale, 1)
        : Number.isNaN(value) || value == null
          ? ''
          : formatNumberByLocale(value, locale, 2);

    const showGrowth = showPercentage
        ? !hasGrowth
            ? '---'
            : formatFractionAsPercentageByLocale(growthNumber, locale, 0)
        : formatNumberByLocale(growthNumber, locale, 2) || '---';

    // One reading for the text and the icon, so a growth right at the limit cannot be
    // "growing" in the tooltip while the arrow points down.
    const direction = describeTrend(value, growth, growthLimit);
    const helpText = trendHelpText(direction, showGrowth, t);
    const [color, Icon] = TREND_ICON[direction];

    // `className` stays on the value rather than going to the tooltip's `targetClassName`:
    // Tooltip renders no target when there is no help text, and the layout of value plus
    // icon has to hold either way.
    return (
        <Tooltip content={helpText} layout="fit">
            <TrendValue
                displayValue={showValue}
                icon={Icon}
                iconColor={color}
                to={to}
                className={className}
            />
        </Tooltip>
    );
}
